import React, { createContext, FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, ConfirmModal } from '@microservices/wiskey-react-components'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { Box, Grid, Typography } from '@mui/material'
import { DataGridProps } from '@mui/x-data-grid'

import { useMapsConfigs } from '@pages/MapsConfigs.tsx/hooks'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { ExportActionsPanel } from '@components/ConfigActionsPanel'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { CopyDialog } from '@components/CopyDialog'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { PageTitle } from '@components/PageTitle'
import { ExportAllCheckbox } from '@components/ui/ExportAllCheckbox'

import { EditConfigProvider, useEditConfig } from '@providers/EditConfigProvider'

import { generateConfigSelectColumn, generateDataGridActionsColumn } from '@helpers'
import { CONFIG_MENU_OPTION, ENTITY_CONFIG_TYPE, FLEXIBLE } from '@constants'

export const PageContext = createContext<any>({})

export const MapsConfigs = () => {
  const { t } = useTranslation()

  const { configMenuOption, selectedConfigId, handleSelectConfigId } = useEditConfig()

  const columns: DataGridProps['columns'] = [
    {
      field: 'code',
      headerName: t('maps.internalId'),
      flex: 1,
    },
    {
      field: 'title',
      headerName: t('maps.title'),
      flex: 1,
    },
  ]

  const {
    state: {
      showCopyDialog,
      idToDelete,
      copyData,
      isLoadingMapsConfigs,
      isCopyingMapConfig,
      maps,
      mapsConfigs,
    },
    handlers: {
      handleCreateMap,
      handleEditMap,
      handleDeleteMap,
      handleCloseDeleteModal,
      handleShowDeleteModal,
      handleCopyMap,
      handleShowCopyDialog,
      handleCloseCopyDialog,
    },
  } = useMapsConfigs()

  const contextValue = {
    maps,
  }

  const columnsWithActions = useMemo(
    () =>
      [
        ...columns,
        ...(configMenuOption && configMenuOption !== CONFIG_MENU_OPTION.IMPORT
          ? [
              generateConfigSelectColumn({
                selectedConfigId,
                onSelectRow: handleSelectConfigId,
              }),
            ]
          : []),
        generateDataGridActionsColumn({
          onEdit: params => handleEditMap(params.row.id),
          onCopy: params => handleShowCopyDialog(params.row.id, params.row.title),
          onDelete: params => handleShowDeleteModal(params.row.id),
        }),
      ].filter(Boolean),
    [columns, selectedConfigId, configMenuOption]
  )

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigScriptDialog />
      {showCopyDialog && copyData && (
        <CopyDialog
          hideObject
          copyData={copyData}
          dialogTitle={t(`copying.map.dialogTitle`)}
          isShow={showCopyDialog}
          loading={isCopyingMapConfig}
          models={maps?.map(map => map.code) || []}
          onClose={handleCloseCopyDialog}
          onCopy={handleCopyMap}
        />
      )}
      {Boolean(idToDelete) && (
        <ConfirmModal
          isShow
          actionBtnText={t('modal.delete.btn')}
          text={t('modal.delete.warning')}
          title={t('modal.delete.title')}
          containerStyle={{
            px: 0.5,
            py: 0.5,
          }}
          onClose={handleCloseDeleteModal}
          onConfirm={handleDeleteMap}
        />
      )}
      <>
        <PageTitle
          end={
            <Box sx={{ py: 0.5 }}>
              <EditConfigMenu />
            </Box>
          }
          start={
            <>
              <Typography display={'inline'} sx={{ mr: 1, ml: 1 }} variant={'h6'}>
                {t('pageTitle.maps')}
              </Typography>
            </>
          }
        />
        <PageContentLayout sx={{ ...FLEXIBLE, p: 1 }}>
          <ExportAllCheckbox />
          <Grid container alignItems='center' flexWrap='nowrap' gap={1}>
            <Grid item>
              <Button sx={{ mb: 1, width: 150 }} variant={'contained'} onClick={handleCreateMap}>
                {t(`maps.add`)}
              </Button>
            </Grid>
          </Grid>
          <DataGrid
            hideFooter
            columns={columnsWithActions}
            loading={isLoadingMapsConfigs}
            rows={mapsConfigs || []}
            sx={FLEXIBLE}
            onDelete={handleShowDeleteModal}
          />
          <ExportActionsPanel />
        </PageContentLayout>
      </>
    </PageContext.Provider>
  )
}

export const MapsWithProvider: FC = () => {
  return (
    <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.MAPS}>
      <MapsConfigs />
    </EditConfigProvider>
  )
}
